import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage, documentationRootGuid, projectFilesRootGuid } from '@constants'
import { AxiosError } from 'axios'
import produce from 'immer'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import {
  useGetFileUsages,
  useSetDocumentAttachment,
} from '@api/document-attachments/document-attachments'
import { FileUsageDTO } from '@api/fastAPI.schemas'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles } from '@queries'
import useDeleteDocument from '../../hooks/requests/useDeleteDocument'
import FileUsageRow from './components/FileUsageRow'

interface Props {
  open: boolean
  /** Project file ("Dateien") that could not be deleted */
  fileGuid: string
  fileName?: string
  isDeletingFile: boolean
  onDeleteFile: () => void
  onClose: () => void
}

/**
 * Shown instead of the "still in use" error (MD-CORE-0021) when deleting a
 * project file: the document items showing the file are removed or unlinked
 * here, then the file can be deleted.
 */
const FileUsageDialog = ({
  open,
  fileGuid,
  fileName,
  isDeletingFile,
  onDeleteFile,
  onClose,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    data: usages,
    isFetching,
    refetch,
  } = useGetFileUsages<FileUsageDTO[], AxiosError>(
    projectId as string,
    projectFilesRootGuid,
    fileGuid,
    {
      query: {
        enabled: open,
        onError: error =>
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Verwendungen'), {
            variant: 'error',
          }),
      },
    },
  )

  const {
    mutate: removeItem,
    isLoading: isRemoving,
    variables: removedItemGuid,
  } = useDeleteDocument(documentationRootGuid, { onDeleteSuccess: () => refetch() })

  const {
    mutate: setAttachment,
    isLoading: isUnlinking,
    variables: unlinkVariables,
  } = useSetDocumentAttachment<AxiosError>({
    mutation: {
      onSuccess: file => {
        queryClient.setQueryData<FilesResult>(
          getProjectFiles.getKey(projectId, documentationRootGuid),
          produce<FilesResult>(draft => {
            if (!draft) return

            const item = draft.files.find(({ guid }) => guid === file.guid)
            if (item) {
              item.attachment = null
              item.url = file.url
            }
          }),
        )
        refetch()
      },
      onError: error =>
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Lösen der Datei'), {
          variant: 'error',
        }),
    },
  })

  const isBusy = isRemoving || isUnlinking || isDeletingFile
  const isUnused = usages?.length === 0 && !isFetching

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // Rendered inside list rows: clicks must not reach them through the portal
      onClick={event => event.stopPropagation()}
      data-cy="file-usage-dialog"
    >
      <DialogTitle>Datei wird noch verwendet</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {fileName ? `„${fileName}“ wird` : 'Die Datei wird'} noch in der Dokumentation verwendet.
          Entferne die Einträge oder löse ihre Verknüpfung, um die Datei zu löschen.
        </Typography>

        {!usages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : usages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-cy="file-usage-none">
            Die Datei wird nicht mehr verwendet und kann gelöscht werden.
          </Typography>
        ) : (
          usages.map(usage => (
            <FileUsageRow
              key={usage.guid}
              usage={usage}
              disabled={isBusy || isFetching}
              isRemoving={isRemoving && removedItemGuid === usage.guid}
              isUnlinking={isUnlinking && unlinkVariables?.fileGuid === usage.guid}
              onRemove={() => removeItem(usage.guid)}
              onUnlink={() =>
                setAttachment({
                  projectGuid: projectId as string,
                  rootFolderGuid: documentationRootGuid,
                  fileGuid: usage.guid,
                  data: { attachment_guid: null },
                })
              }
            />
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose} disabled={isBusy} data-cy="btn-file-usage-cancel">
          Abbrechen
        </Button>
        <LoadingButton
          size="small"
          variant="contained"
          color="error"
          onClick={onDeleteFile}
          loading={isDeletingFile}
          disabled={!isUnused || isRemoving || isUnlinking}
          data-cy="btn-file-usage-delete-file"
        >
          Datei löschen
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default FileUsageDialog
